import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useBranding } from '../lib/branding';

/**
 * La marca que ve el paciente: la de su programa, con la de la institución abajo.
 *
 * El `BrandingProvider` carga la de la institución a partir de la dirección, y lo
 * hace **antes de que haya sesión** — es lo que pinta la pantalla de ingreso.
 * Una vez adentro eso deja de alcanzar: la persona se inscribió en el
 * acompañamiento de una marca, no en una institución, y es ese nombre y ese
 * color lo que reconoce.
 *
 * Va por `useQuery` y no por `getQueryData`: lo segundo lee la caché una vez y
 * **no vuelve a renderizar cuando llega el dato**. En una carga fría el menú se
 * quedaba con el nombre de la institución para siempre, al lado de un cuerpo que
 * ya decía el del programa. Con la misma clave que usa la pantalla de inicio no
 * hay pedido de más: es la misma consulta compartida.
 */
interface HomeBranding {
  branding: { programName?: string; logoUrl?: string | null } | null;
}

export function useProgramBranding(): { programName: string; logoUrl: string | null } {
  const tenant = useBranding();

  const home = useQuery({
    queryKey: ['me', 'home'],
    queryFn: () => api.get<HomeBranding>('/me/home'),
    staleTime: 30_000,
  });

  return {
    programName: home.data?.branding?.programName ?? tenant.programName,
    logoUrl: home.data?.branding?.logoUrl ?? tenant.branding?.logoUrl ?? null,
  };
}
